import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { AdminModal, BTN_BLACK, BTN_WHITE, errText } from "./AdminModal";
import { ShareTools } from "./LeadShareTools";
import { FieldGroups } from "./LeadShareFields";
import { CustomFields } from "./LeadShareCustom";
import { AnswerFormats } from "./LeadShareFormats";
import { LeadSharePreview } from "./LeadSharePreview";

const METHOD = {
  email: "Lead email", webhook: "Web post", crm_salesforce: "Salesforce", crm_litify: "Litify", crm_lawmatics: "Lawmatics",
  crm_lawruler: "Law Ruler", crm_leaddocket: "Lead Docket", crm_hubspot: "HubSpot", crm_zoho: "Zoho",
  crm_pipedrive: "Pipedrive", crm_zapier: "Zapier", s3: "File drop (S3)", ftp: "File drop (FTP)",
};
const EMPTY = { names: {}, custom_fields: [], formats: {}, field_formats: {} };
const OWN = ["hidden", "names", "custom_fields", "formats", "field_formats"];
const pick = (o) => Object.fromEntries(OWN.map((k) => [k, o[k]]));

function Methods({ buyer }) {
  if (!buyer?.methods?.length) return null;
  return (
    <p className="mb-3 text-[11px]" data-testid="lead-share-methods">
      This list is used for every way <b>{buyer.name}</b> gets leads:{" "}
      <b>{[...new Set(buyer.methods.map((m) => METHOD[m.type] || m.type))].join(" · ")}</b>.
    </p>
  );
}

// destId null = the shared default used by every buyer that doesn't have its own list.
export function LeadShareDialog({ destId = null, title, onClose }) {
  const url = destId ? `/admin/lead-share/destinations/${destId}` : "/admin/lead-share/default";
  const [cat, setCat] = useState(null);
  const [form, setForm] = useState(null);
  const [def, setDef] = useState(null);
  const [buyer, setBuyer] = useState(null);
  const [others, setOthers] = useState([]);
  const [q, setQ] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let alive = true;
    Promise.all([api.get("/admin/lead-fields/catalog"), api.get(url), api.get("/admin/lead-share/buyers")])
      .then(([c, r, b]) => {
        if (!alive) return;
        const d = r.data;
        setCat(c.data);
        setForm({ inherit: destId ? d.inherit : false, ...pick(d) });
        setDef(d.default || null);
        setBuyer(d.buyer || null);
        setOthers(b.data.buyers.filter((x) => x.key !== d.buyer?.key));
      })
      .catch((e) => { if (alive) { toast.error(errText(e, "Couldn't load lead fields")); onClose(); } });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]);

  const inheriting = Boolean(destId && form?.inherit && def);
  const shown = !form ? null : inheriting ? { hidden: def.hidden, ...EMPTY } : form;
  // Any change gives this buyer its own list, starting from what is shown.
  const change = (fn) => setForm((f) => {
    const b = destId && f.inherit && def ? { hidden: def.hidden, ...EMPTY } : f;
    return { ...b, ...fn(b), inherit: false };
  });
  const toggle = (k) => change((b) => ({ hidden: b.hidden.includes(k) ? b.hidden.filter((x) => x !== k) : [...b.hidden, k] }));
  const rename = (k, v) => change((b) => ({ names: { ...b.names, [k]: v } }));
  const setGroup = (g, send) => change((b) => {
    const keys = g.fields.map((x) => x.key);
    const rest = b.hidden.filter((x) => !keys.includes(x));
    return { hidden: send ? rest : [...rest, ...keys] };
  });
  const copy = (o) => {
    change(() => ({ ...structuredClone(pick(o)) }));
    toast.success(`Copied ${o.name}'s choices and names — press Save to keep them`);
  };
  const setInherit = (v) => setForm((f) => (v || !def ? { ...f, inherit: v } : { inherit: false, hidden: [...def.hidden], ...EMPTY }));
  const save = async () => {
    setSaving(true);
    try {
      await api.put(url, destId ? form : pick(form));
      toast.success("Lead fields saved");
      onClose();
    } catch (e) {
      toast.error(errText(e, "Couldn't save lead fields"));
    } finally {
      setSaving(false);
    }
  };

  const footer = (
    <>
      <button type="button" className={BTN_WHITE} onClick={onClose} data-testid="lead-share-cancel">Cancel</button>
      <button type="button" className={BTN_BLACK} onClick={save} disabled={!form || saving} data-testid="lead-share-save">
        {saving ? "Saving…" : "Save lead fields"}
      </button>
    </>
  );
  return (
    <AdminModal eyebrow="Lead fields sent to buyers" title={title || "Default for every buyer"} onClose={onClose} footer={footer} testId="lead-share-modal" wide>
      {!form || !cat ? <div className="label-mono py-4">Loading…</div> : (
        <>
          <p className="mb-3 text-[11px] text-zinc-600">
            Ticked details are sent by every method — web posts, CRMs, file drops and lead emails. Unticked details stay on the lead here; they&apos;re only left out of what the buyer gets.
          </p>
          {destId && <Methods buyer={buyer} />}
          {destId && (
            <label className="mb-3 flex items-center justify-between gap-3 border-2 border-black bg-zinc-50 p-2 text-sm">
              <span>Use the default list for every buyer <span className="text-[11px] text-zinc-500">(change any tick or name to give this buyer its own)</span></span>
              <Switch checked={form.inherit} onCheckedChange={setInherit} data-testid="lead-share-inherit" />
            </label>
          )}
          <div className="grid grid-cols-[minmax(0,1fr)] gap-5 lg:grid-cols-[minmax(0,1fr)_340px]">
            <div className="min-w-0">
              <ShareTools cat={cat} hidden={shown.hidden} onPreset={(p) => change(() => ({ hidden: [...p.hidden] }))} others={others} onCopy={copy} q={q} setQ={setQ} />
              {!q && (
                <AnswerFormats cat={cat} formats={shown.formats} fieldFormats={shown.field_formats} def={destId ? def : null}
                               onFormats={(v) => change(() => ({ formats: v }))} onFieldFormats={(v) => change(() => ({ field_formats: v }))} />
              )}
              <FieldGroups cat={cat} rule={shown} defNames={destId ? def?.names || {} : {}} q={q} onToggle={toggle} onRename={rename} onGroup={setGroup} />
              {!q && (
                <CustomFields cat={cat} rows={shown.custom_fields} inherited={destId ? def?.custom_fields || [] : []}
                              onChange={(list) => change(() => ({ custom_fields: list }))} />
              )}
            </div>
            <LeadSharePreview rule={inheriting ? def : shown} layer={Boolean(destId && !inheriting)} destId={destId} />
          </div>
        </>
      )}
    </AdminModal>
  );
}
